import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import { FileDownload as DownloadIcon } from '@mui/icons-material';

import StatsCard from '../components/StatsCard';
import { fetchHistory, fetchDevices, fetchStats, exportCSV } from '../services/api';

function History() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [devices, setDevices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(0);

  // Filters
  const [gpsFilter, setGpsFilter] = useState('');
  const [daysFilter, setDaysFilter] = useState(7);
  const [limitFilter, setLimitFilter] = useState(1000);

  // Load devices and stats on mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [devicesData, statsData] = await Promise.all([
          fetchDevices(),
          fetchStats(),
        ]);
        setDevices(devicesData.devices || []);
        setStats(statsData);
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };
    loadInitialData();
  }, []);

  // Load history
  const loadHistory = async () => {
    setLoading(true);
    try {
      const result = await fetchHistory({
        gps: gpsFilter,
        days: daysFilter,
        limit: limitFilter,
      });
      setHistory(result.data || []);
      setCount(result.count || 0);
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadHistory();
  }, []);

  const handleExport = () => {
    exportCSV({ gps: gpsFilter, days: daysFilter });
  };

  return (
    <Box>
      {/* Tabs — match the rovers page (Devices lives on the GPS Systems dashboard) */}
      <Tabs
        value={1}
        onChange={(_, v) => { if (v === 0) navigate('/equipment/machines/gps-health'); }}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Devices" />
        <Tab label="History" />
      </Tabs>

      {/* Filters Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Filters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>GPS Device</InputLabel>
                <Select
                  value={gpsFilter}
                  label="GPS Device"
                  onChange={(e) => setGpsFilter(e.target.value)}
                >
                  <MenuItem value="">All Devices</MenuItem>
                  {devices.map((device) => (
                    <MenuItem key={device} value={device}>
                      {device}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Time Range</InputLabel>
                <Select
                  value={daysFilter}
                  label="Time Range"
                  onChange={(e) => setDaysFilter(e.target.value)}
                >
                  <MenuItem value={1}>Last 24 hours</MenuItem>
                  <MenuItem value={7}>Last 7 days</MenuItem>
                  <MenuItem value={30}>Last 30 days</MenuItem>
                  <MenuItem value={90}>Last 90 days</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Max Records</InputLabel>
                <Select
                  value={limitFilter}
                  label="Max Records"
                  onChange={(e) => setLimitFilter(e.target.value)}
                >
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={500}>500</MenuItem>
                  <MenuItem value={1000}>1,000</MenuItem>
                  <MenuItem value={5000}>5,000</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={5}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                <Button variant="contained" color="primary" onClick={loadHistory}>
                  Apply Filters
                </Button>
                <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport}>
                  Export CSV
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Stats Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4}>
          <StatsCard label="Total Issues" value={stats?.total_records?.toLocaleString() || '-'} />
        </Grid>
        <Grid item xs={6} sm={4}>
          <StatsCard label="Showing" value={count.toLocaleString()} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatsCard
            label="Date Range"
            value={
              stats?.oldest_record && stats?.newest_record
                ? `${stats.oldest_record.split(' ')[0]} to ${stats.newest_record.split(' ')[0]}`
                : '-'
            }
          />
        </Grid>
      </Grid>

      {/* History Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : history.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No historical data found for the selected filters.
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Column header — matches the Devices table */}
          <Paper
            sx={{
              display: { xs: 'none', md: 'block' },
              mb: 0.75, px: 2, py: 1,
              backgroundColor: 'secondary.main',
              color: 'white',
              borderRadius: 1,
            }}
          >
            <Grid container spacing={2} alignItems="center" sx={{ flexWrap: 'nowrap' }}>
              <Grid item sx={{ flexShrink: 0, width: 200 }}>
                <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Timestamp</Typography>
              </Grid>
              <Grid item sx={{ flexShrink: 0, width: 120 }}>
                <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>GPS</Typography>
              </Grid>
              <Grid item sx={{ flexShrink: 0, width: 200 }}>
                <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>IP</Typography>
              </Grid>
              <Grid item sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Issue</Typography>
              </Grid>
              <Grid item sx={{ flexShrink: 0, width: 120, textAlign: 'right' }}>
                <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Duration</Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* One card per record — matches the Devices table rows; ongoing
              issues get the red border treatment instead of a tinted row */}
          {history.map((row, index) => (
            <Card key={index} sx={{ mb: 0.75, border: 1, borderColor: row.ongoing ? 'error.main' : 'divider' }}>
              <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                <Grid container spacing={2} alignItems="center" sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                  <Grid item xs={6} md="auto" sx={{ flexShrink: 0, width: { md: 200 } }}>
                    <Typography variant="body2" color="text.secondary">{row.timestamp}</Typography>
                  </Grid>
                  <Grid item xs={6} md="auto" sx={{ flexShrink: 0, width: { md: 120 } }}>
                    <Typography variant="body1" fontWeight={700} color="primary.main">{row.gps_num}</Typography>
                  </Grid>
                  <Grid item xs={6} md="auto" sx={{ flexShrink: 0, width: { md: 200 } }}>
                    <Typography variant="body2" noWrap>{row.ip_address}</Typography>
                  </Grid>
                  <Grid item xs={6} md="auto" sx={{ flexShrink: 0, width: { md: 120 }, textAlign: { xs: 'right', md: 'left' } }}>
                    <Typography variant="body2">{row.duration}</Typography>
                  </Grid>
                  {/* Issue names run long (e.g. NtripStatusDisconnectedByCaster) —
                      full width and breakable on a phone so nothing is cut off. */}
                  <Grid item xs={12} md sx={{ flex: { md: 1 }, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{row.issue}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </>
      )}
    </Box>
  );
}

export default History;
